<script setup>
import { Form, FormItem } from '@dashhudson/dashing-ui/form';
import { InputText } from '@dashhudson/dashing-ui/input-text';
import { ToggleSwitch } from '@dashhudson/dashing-ui/toggle-switch';
import { helpers, maxLength, minLength, required } from '@vuelidate/validators';
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';

import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { ONE_WORD_QUOTED_KEYWORD_PATTERN } from '@/app/community/utils/keyword-filters';

const props = defineProps({
  modelValue: { type: String, default: '' },
  unavailableValues: { type: Array, default: () => [] },
  isValid: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'update:isValid', 'valid', 'commandName']);
const { canAccessInitiateDM } = useCommunityPermissions();

const KEYWORD_TOOLTIP =
  '• Matching is not case-sensitive\n' +
  '• Keywords match words with up to 3 additional characters at the end (e.g., ‘delay’ matches ‘delays’ and ‘delayed’)\n' +
  '• When toggle is enabled, results include only messages containing that exact keyword and nothing else';

function parseKeywordValue(value = '') {
  const raw = value ?? '';
  if (!canAccessInitiateDM.value) {
    return {
      keyword: raw,
      oneWordMessagesOnly: false,
    };
  }

  const trimmed = raw.trim();
  const quotedMatch = trimmed.match(ONE_WORD_QUOTED_KEYWORD_PATTERN);
  if (quotedMatch) {
    return {
      keyword: quotedMatch[1],
      oneWordMessagesOnly: true,
    };
  }
  return {
    keyword: raw,
    oneWordMessagesOnly: false,
  };
}

function serializeKeywordValue(value = {}) {
  const keyword = value?.keyword ?? '';
  if (!canAccessInitiateDM.value) {
    return keyword;
  }

  if (!value?.oneWordMessagesOnly) {
    return keyword;
  }

  const trimmedKeyword = keyword.trim();
  if (!trimmedKeyword) {
    return '';
  }
  return `"${trimmedKeyword}"`;
}

const formValue = ref(parseKeywordValue(props.modelValue));
const defaultValue = ref(parseKeywordValue(props.modelValue));
const lastEmittedModelValue = ref();
const keywordTooltip = computed(() => (canAccessInitiateDM.value ? KEYWORD_TOOLTIP : undefined));

const KEYWORD_HELP_TEXT = computed(() =>
  !canAccessInitiateDM.value || formValue.value?.oneWordMessagesOnly
    ? null
    : 'Matches messages that contain the keyword or exact phrase.',
);

const trimmedKeyword = computed(() => {
  const { keyword } = formValue.value ?? {};
  return (keyword ?? '').trim();
});
const serializedKeyword = computed(() => serializeKeywordValue(formValue.value));
const keywordForDuplicateCheck = computed(() =>
  canAccessInitiateDM.value && formValue.value?.oneWordMessagesOnly
    ? serializedKeyword.value
    : trimmedKeyword.value,
);
const unavailableValuesSet = computed(
  () => new Set((props.unavailableValues ?? []).map((value) => (value ?? '').trim())),
);

const rules = computed(() => ({
  keywordExists: helpers.withMessage(
    'Keyword already exists',
    () => !unavailableValuesSet.value.has(keywordForDuplicateCheck.value),
  ),
  required: helpers.withMessage('Keyword cannot be empty', required),
  shouldNotEmpty: helpers.withMessage('Keyword cannot be empty', minLength(1)),
  maxLength: helpers.withMessage('Keyword cannot exceed 50 characters', maxLength(50)),
  ...(canAccessInitiateDM.value
    ? {
        oneWordOnly: helpers.withMessage(
          'Enter a single keyword only',
          () =>
            !formValue.value?.oneWordMessagesOnly ||
            trimmedKeyword.value === '' ||
            !/\s/.test(trimmedKeyword.value),
        ),
        noQuotations: helpers.withMessage(
          'Quotes aren’t supported. One or more keywords are treated as an exact match by default.',
          () => !trimmedKeyword.value.includes('"'),
        ),
      }
    : {
        invalidDoubleQuote: helpers.withMessage(
          "Use double quotes to isolate a single keyword only. Don't include multiple words or any text outside the quotes.",
          () => {
            const quoteCount = (trimmedKeyword.value.match(/"/g) || []).length;
            if (quoteCount < 2) {
              return true;
            }
            return /^"[^" ]+"$/.test(trimmedKeyword.value);
          },
        ),
      }),
}));

const onValidation = (newVal) => {
  const valid = !newVal.$invalid;
  // Filter button dropdown
  emit('update:isValid', valid);
  // Menu form
  emit('valid', valid);
};

watch(
  () => [formValue.value?.keyword, formValue.value?.oneWordMessagesOnly],
  () => {
    const nextModelValue = serializedKeyword.value;
    lastEmittedModelValue.value = nextModelValue;
    emit('update:modelValue', nextModelValue);
  },
  { immediate: true },
);

watch(
  () => props.modelValue,
  (to) => {
    if (to === lastEmittedModelValue.value) {
      return;
    }
    const parsed = parseKeywordValue(to);
    if (
      formValue.value.keyword !== parsed.keyword ||
      formValue.value.oneWordMessagesOnly !== parsed.oneWordMessagesOnly
    ) {
      formValue.value = parsed;
    }
  },
);

watch(
  () => canAccessInitiateDM.value,
  () => {
    const parsed = parseKeywordValue(props.modelValue);
    if (
      formValue.value.keyword !== parsed.keyword ||
      formValue.value.oneWordMessagesOnly !== parsed.oneWordMessagesOnly
    ) {
      formValue.value = parsed;
    }
  },
);

onBeforeMount(() => {
  emit('commandName', 'Apply');
  defaultValue.value = parseKeywordValue(props.modelValue);
});

onBeforeUnmount(() => {
  formValue.value = { ...defaultValue.value };
});
</script>

<template>
  <Form ref="form" v-model="formValue" class="px-1" @validation="onValidation">
    <div class="flex flex-col gap-4">
      <FormItem
        v-slot="{ inputProps, inputEvents }"
        name="keyword"
        label="Keyword"
        :rules="rules"
        :hint="KEYWORD_HELP_TEXT"
        :tooltip="keywordTooltip"
        required
      >
        <InputText v-bind="inputProps" placeholder="Enter Keyword" v-on="inputEvents" />
      </FormItem>
      <div
        v-if="canAccessInitiateDM"
        class="typo-base text-subtle flex flex-row items-center gap-2"
      >
        <ToggleSwitch v-model="formValue.oneWordMessagesOnly" size="small" />
        <span class="flex flex-row items-center gap-1">
          <span>One-Word Messages Only</span>
        </span>
      </div>
    </div>
  </Form>
</template>
